// ============================================================================
// ACTION TYPE HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { actionTypeService } from '../../api/services/actionTypeService';
import type {
  ActionType,
  ActionTypeListItem,
  CreateActionTypeRequest,
  UpdateActionTypeRequest,
  ActionTypeSearchRequest,
} from '../../api/types/actionType.types';
import type { ID } from '../../api/types';

export const useActionTypes = (params?: ActionTypeSearchRequest) => {
  const [data, setData] = useState<ActionTypeListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const paramsKey = JSON.stringify(params);

  const fetchActionTypes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await actionTypeService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetchActionTypes(); }, [fetchActionTypes]);

  return { data, loading, error, pagination, refetch: fetchActionTypes };
};

export const useActionType = (id: ID | undefined) => {
  const [data, setData] = useState<ActionType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchActionType = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await actionTypeService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchActionType(); }, [fetchActionType]);

  return { data, loading, error, refetch: fetchActionType };
};

export const useCreateActionType = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createActionType = async (
    data: CreateActionTypeRequest,
    options?: { onSuccess?: (actionType: ActionType) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await actionTypeService.create(data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { createActionType, loading, error };
};

export const useUpdateActionType = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateActionType = async (
    id: ID, data: UpdateActionTypeRequest,
    options?: { onSuccess?: (actionType: ActionType) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await actionTypeService.update(id, data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { updateActionType, loading, error };
};

export const useDeleteActionType = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteActionType = async (
    id: ID,
    options?: { onSuccess?: () => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      await actionTypeService.delete(id);
      options?.onSuccess?.();
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { deleteActionType, loading, error };
};
